import { DateTime } from 'luxon';

import { checkForRequiredLateReasons, IResponseProfileTimestamp } from './checkForRequiredLateReasons';
import { createRecurringTrip } from './createRecurringTrip';
import { createTrip } from './createTrip';
import { ITripActionData } from './tryUnitAction';

import { container } from '@/container';
import { PriorityManager } from '@/managers/PriorityManager';
import { Logger } from '@/services/Logger';
import { checkIsLateReasonRequiredForRange } from '@/utils/lateReasonHelper';

type DispatchCallback = (action: string, payload: unknown) => Promise<unknown>;

export async function tryTripCreateAssignAction(
  data: ITripActionData,
  dispatch?: DispatchCallback,
): Promise<{ response: unknown; lateReasonInfo: unknown }> {
  const startMs = performance.now();
  const priorityManager = container.get(PriorityManager);
  const logger = container.get(Logger);

  let response;
  const responseProfile = priorityManager.find(data.trip.priority.id)?.responseProfileInfo;
  const timestamps = (responseProfile?.responseTimeStamp as unknown as IResponseProfileTimestamp[]) ?? [];
  let timestamp = null;
  let reasonPrompt = false;
  let lateReasonInfo = null;
  data.trip.pickupDateTime = DateTime.fromFormat(
    `${(data.trip as Record<string, unknown>).pickupDate} ${(data.trip as Record<string, unknown>).pickupTime}`,
    'yyyy-MM-dd HH:mm',
  ).toUTC().toISO() || '';

  if (Number(data.toStatusId) > (Number(data.fromStatusId) || 0)) {
    if (timestamps.length > 0) {
      for (let step = Number(data.fromStatusId) + 1; step <= Number(data.toStatusId); step += 1) {
        timestamp = timestamps.find((item) => item.status === Number(step) && item.lateCapture === 1);

        const isTimestampLateRequired = checkForRequiredLateReasons({
          trip: data.trip,
          responseProfileTimestamp: timestamp,
        });

        if (isTimestampLateRequired) {
          lateReasonInfo = { ...data, responseProfileTimestamp: timestamp };
          reasonPrompt = true;
          break;
        }
      }
    }

    if (!reasonPrompt) {
      const isLateRequired = checkIsLateReasonRequiredForRange(
        data.trip,
        data.fromStatusId || '0',
        data.toStatusId || '0',
      );

      if (isLateRequired) {
        lateReasonInfo = { ...data, responseProfileTimestamp: timestamp };
        reasonPrompt = true;
      }
    }
  }

  if (!reasonPrompt) {
    if (data.dispatchAction === 'createTrip') {
      response = await createTrip({ trip: data.trip as Record<string, unknown> });
    }
    else if (data.dispatchAction === 'createRecurringTrip') {
      response = await createRecurringTrip({ trip: data.trip as Record<string, unknown> });
    }
    else if (dispatch) {
      response = await dispatch(data.dispatchAction, data.trip);
    }

    logger.track('trip.create_assign', {
      durationMs: Math.round(performance.now() - startMs),
      dispatchAction: data.dispatchAction,
      fromStatus: data.fromStatusId ?? '0',
      toStatus: data.toStatusId ?? '0',
      hadLateReasonPrompt: false,
      success: true,
    });
  }

  return { response, lateReasonInfo };
}
